import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Button, SegmentedControl } from '@CommonComponents/buttons'
import { HotspotStage, REGION_STYLES, RegionLabel, RegionShape } from './HotspotStage'
import { coordsFromDrag, hitRegion, pointerPercent } from './geometry'

/**
 * Region editor for the hotspot question form. Reads and writes the JSON in the form's hidden
 * input: { image: { fileitem, alt, url }, regions: [{ guid, label, correct, shape, coords }] }.
 * The image comes from the course image gallery dialog (instructor/scripts/gallery_dialog.jsm),
 * which triggers 'gallery-image-selected' on the mount element.
 */

const TOOLS = [
  { value: 'select', label: 'Select' },
  { value: 'rect', label: 'Rectangle' },
  { value: 'ellipse', label: 'Ellipse' },
  { value: 'polygon', label: 'Polygon' },
]
const TOOL_HINTS = {
  select: 'Click a region to highlight it in the list.',
  rect: 'Drag on the image to draw a rectangle.',
  ellipse: 'Drag on the image to draw an ellipse.',
  polygon: 'Click to add points. Click the first point or press Enter to finish; Escape cancels.',
}
const SELECTED_OUTLINE = { strokeWidth: 4, strokeDasharray: '6 3', fillOpacity: 0.6 }
const MIN_SIZE = 0.5
const CLOSE_DISTANCE = 2

let nextKey = 1
const withKey = region => ({ ...region, key: `r${nextKey++}` })

function readInput(input) {
  try {
    const data = JSON.parse(input.value || '{}')
    return {
      image: { fileitem: '', alt: '', url: '', ...(data.image || {}) },
      regions: (data.regions || []).map(withKey),
    }
  } catch {
    return { image: { fileitem: '', alt: '', url: '' }, regions: [] }
  }
}

const stopEnter = e => { if (e.key === 'Enter') e.preventDefault() }

export const HotspotEditor = ({ mountEl, inputId, mountId, coursenum }) => {
  const input = document.getElementById(inputId)
  const [initial] = useState(() => readInput(input))
  const [image, setImage] = useState(initial.image)
  const [regions, setRegions] = useState(initial.regions)
  const [tool, setTool] = useState('select')
  const [selectedKey, setSelectedKey] = useState(null)
  const [drag, setDrag] = useState(null)           // { start, end } while drawing a rect/ellipse
  const [polygon, setPolygon] = useState([])       // points of the polygon in progress
  const stageRef = useRef(null)

  // Keep the hidden input in sync; that's what the form posts.
  useEffect(() => {
    input.value = JSON.stringify({
      image,
      regions: regions.map(({ guid, label, correct, shape, coords }) => ({ guid, label, correct, shape, coords })),
    })
  }, [image, regions])

  useEffect(() => {
    const $el = window.jQuery(mountEl)
    $el.on('gallery-image-selected', (e, data) => {
      setImage(img => ({ ...img, fileitem: String(data.pk), url: data.url }))
    })
    return () => $el.off('gallery-image-selected')
  }, [mountEl])

  // open2 (a Bootstrap modal) rather than the CKEditor plugin's jQuery UI dialog, whose close
  // button has no icon and which the gallery's own close2 calls can't close.
  const openGallery = () => {
    window.jQuery.meDialog.open2({
      id: 'dialog_insert_picture',
      url: `/instructor/gallery_dialog/${coursenum}/${mountId}`,
      title: 'Image Gallery',
      width: 900,
    })
  }

  const addRegion = useCallback((shape, coords) => {
    const region = withKey({ guid: '', label: `Region ${regions.length + 1}`, correct: false, shape, coords })
    setRegions(rs => [...rs, region])
    setSelectedKey(region.key)
  }, [regions.length])

  const updateRegion = (key, changes) => setRegions(rs => rs.map(r => (r.key === key ? { ...r, ...changes } : r)))
  const deleteRegion = key => {
    setRegions(rs => rs.filter(r => r.key !== key))
    if (selectedKey === key) setSelectedKey(null)
  }

  const finishPolygon = useCallback(() => {
    if (polygon.length >= 3) addRegion('polygon', polygon.flatMap(p => [p.x, p.y]))
    setPolygon([])
  }, [polygon, addRegion])

  // The segmented control expects a request; picking a tool is local, so it resolves at once.
  const chooseTool = key => {
    setPolygon([])
    setDrag(null)
    setTool(key)
    return Promise.resolve()
  }

  const onPointerDown = e => {
    if (e.button !== 0) return
    const point = pointerPercent(e, stageRef.current)
    if (tool === 'select') {
      const region = hitRegion(regions, point.x, point.y)
      setSelectedKey(region ? region.key : null)
    } else if (tool === 'polygon') {
      const first = polygon[0]
      if (first && polygon.length >= 3 && Math.hypot(point.x - first.x, point.y - first.y) <= CLOSE_DISTANCE) {
        finishPolygon()
      } else {
        setPolygon(ps => [...ps, point])
      }
    } else {
      stageRef.current.setPointerCapture?.(e.pointerId)
      setDrag({ start: point, end: point })
    }
  }

  const onPointerMove = e => {
    if (drag) setDrag(d => ({ ...d, end: pointerPercent(e, stageRef.current) }))
  }

  const onPointerUp = () => {
    if (!drag) return
    const coords = coordsFromDrag(tool, drag.start, drag.end)
    const size = tool === 'ellipse' ? [coords[2] * 2, coords[3] * 2] : [coords[2], coords[3]]
    if (size[0] >= MIN_SIZE && size[1] >= MIN_SIZE) addRegion(tool, coords.map(c => Math.round(c * 100) / 100))
    setDrag(null)
  }

  const onStageKeyDown = e => {
    if (tool !== 'polygon') return
    if (e.key === 'Enter') { e.preventDefault(); finishPolygon() }
    if (e.key === 'Escape') { e.preventDefault(); setPolygon([]) }
  }

  const draft = drag
    ? { shape: tool, coords: coordsFromDrag(tool, drag.start, drag.end) }
    : polygon.length ? { shape: 'polygon', coords: polygon.flatMap(p => [p.x, p.y]) } : null

  return (
    <div className="hotspot-editor" style={{ fontFamily: 'var(--me-font-ui)' }}>
      <div className="d-flex align-items-center flex-wrap mb-2" style={{ gap: '0.5rem' }}>
        <Button className="me-btn me-btn-secondary me-btn-sm" icon="fa-regular fa-image" onClick={openGallery}>
          {image.url ? 'Change image' : 'Choose image'}
        </Button>
        <label className="mb-0 d-flex align-items-center flex-grow-1" style={{ gap: '0.5rem' }}>
          <span className="text-nowrap">Alt text:</span>
          <input
            type="text"
            className="form-control"
            style={{ flex: '1 1 auto', width: 'auto', maxWidth: 'none' }}
            value={image.alt}
            placeholder="Describe the image for screen reader users"
            onChange={e => setImage(img => ({ ...img, alt: e.target.value }))}
            onKeyDown={stopEnter}
          />
        </label>
      </div>

      {image.url ? (
        <>
          <div className="mb-2">
            <SegmentedControl
              small
              label="Drawing tool"
              value={tool}
              options={TOOLS}
              onChange={chooseTool}
              data-success-label="Drawing tool changed"
              data-failure-label="Drawing tool could not be changed"
            />
          </div>
          {/* One hint line for every tool, so switching tools doesn't shift the image. */}
          <div className="d-flex align-items-center text-muted small mb-2" style={{ minHeight: 30, gap: '0.5rem' }}>
            <span>{TOOL_HINTS[tool]}</span>
            {tool === 'polygon' && polygon.length >= 3 && (
              <Button className="me-btn me-btn-ghost me-btn-xs" onClick={finishPolygon}>Finish polygon</Button>
            )}
          </div>
          <div className="d-flex flex-wrap" style={{ gap: '1rem', alignItems: 'flex-start' }}>
            <HotspotStage
              imageUrl={image.url}
              imageAlt={image.alt}
              stageRef={stageRef}
              stageProps={{
                tabIndex: 0,
                onPointerDown,
                onPointerMove,
                onPointerUp,
                onKeyDown: onStageKeyDown,
                style: { cursor: tool === 'select' ? 'default' : 'crosshair', touchAction: 'none', maxWidth: 640 },
              }}
              overlay={
                <>
                  {regions.map(region => (
                    <RegionShape
                      key={region.key}
                      region={region}
                      style={{
                        ...(region.correct ? REGION_STYLES.correct : REGION_STYLES.editing),
                        // selection keeps the region's color and adds a marquee
                        ...(region.key === selectedKey ? SELECTED_OUTLINE : {}),
                      }}
                    />
                  ))}
                  {draft && <RegionShape region={draft} style={REGION_STYLES.selected} />}
                </>
              }
            >
              {regions.map(region => <RegionLabel key={region.key} region={region} />)}
            </HotspotStage>

            <div style={{ flex: '1 1 260px', minWidth: 240 }}>
              <div className="font-weight-bold mb-1">Regions <span className="text-muted font-weight-normal">(check the correct ones)</span></div>
              {regions.length === 0 && <div className="text-muted">Pick a shape tool and draw on the image.</div>}
              {regions.map(region => (
                <div
                  key={region.key}
                  className="d-flex align-items-center mb-1 p-1"
                  style={{
                    gap: '0.4rem', borderRadius: 4,
                    backgroundColor: region.key === selectedKey ? 'var(--brand-tint)' : 'transparent',
                  }}
                  onFocus={() => setSelectedKey(region.key)}
                >
                  <input
                    type="checkbox"
                    checked={region.correct}
                    aria-label={`${region.label} is correct`}
                    onChange={e => updateRegion(region.key, { correct: e.target.checked })}
                  />
                  <input
                    type="text"
                    className="form-control input-sm"
                    value={region.label}
                    aria-label="Region label"
                    onChange={e => updateRegion(region.key, { label: e.target.value })}
                    onKeyDown={stopEnter}
                  />
                  <Button
                    className="me-btn-icon me-btn-icon-sm"
                    icon="fa-regular fa-trash-can"
                    aria-label={`Delete ${region.label}`}
                    data-tooltip={`Delete ${region.label}`}
                    onClick={() => deleteRegion(region.key)}
                  />
                </div>
              ))}
              <div className="text-muted small mt-2">
                Labels show only in feedback and in the list students can choose from instead of clicking.
                Where regions overlap, the one lower in this list wins.
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-muted">Choose an image to start drawing regions.</div>
      )}
    </div>
  )
}
